import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  deleteCourseCertificateAttachment,
  fetchCourseCertificateAttachmentFile,
  uploadCourseCertificateAttachment,
} from "@/api/courseCertificates";
import { createSubjectReviewQueryKey } from "@/api/documentReview";
import {
  deleteTeachingAttachment,
  fetchTeachingAttachmentFile,
  uploadTeachingAttachment,
} from "@/api/teachings";
import {
  invalidateCourseCertificateQueries,
  invalidateTeachingQueries,
} from "@/features/cle-compliance/hooks/invalidateCleDocumentQueries";
import type {
  EvidenceAttachmentModel,
  SupplementaryDocumentKind,
} from "@/features/cle-compliance/models/evidenceAttachment";

/* --- Supplerende dokumentation (bilag) ----------------------------------
 *
 * Kursusbeviser og undervisning har de samme bilag (ADV-359), bare under hver
 * sin rute. Hookene tager bevisets type, så kortet kan bruges til begge.
 *
 * Mutationerne invaliderer bevisets egen familie: bilagene ligger på
 * detaljesvaret, så det er dén query, der skal hentes igen. En upload skriver
 * desuden en linje i beskedtråden og sætter beviset til afventende, så tråden
 * hentes også igen. */

type AttachmentApi = {
  upload: (evidenceId: string, file: File) => Promise<EvidenceAttachmentModel>;
  remove: (evidenceId: string, attachmentId: string) => Promise<void>;
  download: (evidenceId: string, attachmentId: string) => Promise<Blob>;
  invalidate: (queryClient: QueryClient) => void;
};

const ATTACHMENT_API: Record<SupplementaryDocumentKind, AttachmentApi> = {
  kursusbevis: {
    upload: uploadCourseCertificateAttachment,
    remove: deleteCourseCertificateAttachment,
    download: fetchCourseCertificateAttachmentFile,
    invalidate: (queryClient) =>
      invalidateCourseCertificateQueries(queryClient),
  },
  undervisning: {
    upload: uploadTeachingAttachment,
    remove: deleteTeachingAttachment,
    download: fetchTeachingAttachmentFile,
    invalidate: (queryClient) => invalidateTeachingQueries(queryClient),
  },
};

type UploadAttachmentParams = {
  evidenceId: string;
  file: File;
};

export const useUploadEvidenceAttachment = (
  kind: SupplementaryDocumentKind,
) => {
  const queryClient = useQueryClient();

  return useMutation<EvidenceAttachmentModel, Error, UploadAttachmentParams>({
    mutationFn: ({ evidenceId, file }) =>
      ATTACHMENT_API[kind].upload(evidenceId, file),
    onSuccess: (_attachment, { evidenceId }) => {
      ATTACHMENT_API[kind].invalidate(queryClient);
      void queryClient.invalidateQueries({
        queryKey: createSubjectReviewQueryKey(evidenceId),
      });
    },
  });
};

type AttachmentParams = {
  evidenceId: string;
  attachmentId: string;
};

export const useDeleteEvidenceAttachment = (
  kind: SupplementaryDocumentKind,
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, AttachmentParams>({
    mutationFn: ({ evidenceId, attachmentId }) =>
      ATTACHMENT_API[kind].remove(evidenceId, attachmentId),
    onSuccess: () => {
      ATTACHMENT_API[kind].invalidate(queryClient);
    },
  });
};

/** Henter bilaget som blob og lader browseren gemme det.
 *
 * Ikke et almindeligt link: endpointet kræver den autentificerede session, så
 * filen skal hentes gennem api-klienten og derefter gives til brugeren. */
export const useDownloadEvidenceAttachment = (
  kind: SupplementaryDocumentKind,
) =>
  useMutation<Blob, Error, AttachmentParams & { fileName: string }>({
    mutationFn: ({ evidenceId, attachmentId }) =>
      ATTACHMENT_API[kind].download(evidenceId, attachmentId),
    onSuccess: (blob, { fileName }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    },
  });
